import React, { useEffect, useState } from 'react';
import { Transaction } from '../../../api/types';
import { X, Save, Pencil, Copy, Hash, ShieldCheck, CheckCircle } from 'lucide-react';
import { transactionApi } from '../../../api/transactionApi';
import stripeApi from '../../../api/stripeApi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import formatCurrency from '../../../utils/formatCurrency';
import VendorAliasModal from '../ap/VendorAliasModal';
import NormalizedDescriptionModal from '../components/NormalizedDescriptionModal';
import StandardAccountSelector from '../components/StandardAccountSelector';
import QBOAccountSelector from '../components/QBOAccountSelector';
import { StandardAccount, QboAccount } from '../../../api/types';
import { getCategoryBadgeColor } from '../utils/categoryBadge';
import EditHistoryModal from '../components/EditHistoryModal';
import { History } from 'lucide-react';

interface TransactionDrawerProps {
    transactionId: string | null;
    open: boolean;
    onClose: () => void;
    businessId?: string;
    preloadedTransaction?: Transaction | null;
    normalizationEditorRequest?: {
        token: number;
        matchType?: 'contains' | 'regex' | 'exact' | string;
        matchValue?: string;
        normalizedDescription?: string;
        vendorName?: string;
    } | null;
    onNormalizationEditorRequestConsumed?: () => void;
}

type NormalizationSavedPayload = {
    normalizedDescription: string;
    vendorName?: string;
};

const isLikelyObjectId = (value?: string | null): boolean => {
    const raw = (value || '').trim();
    return /^[a-f0-9]{24}$/i.test(raw);
};

const formatFinzAccountDisplay = (name?: string | null, accountId?: string | null): string => {
    const cleanName = (name || '').trim();
    const cleanId = (accountId || '').trim();
    if (!cleanName) return '---';
    if (!cleanId || isLikelyObjectId(cleanId)) return cleanName;
    return `${cleanName} - ${cleanId}`;
};

const isInternalTransferTransaction = (transaction?: Partial<Transaction> | null): boolean => {
    const bucket = (transaction?.fiscal_bucket || '').trim().toUpperCase();
    const category = (transaction?.category || '').trim().toUpperCase();
    const finzName = (transaction?.finz_account_name || '').trim().toUpperCase();
    return (
        category === 'INTERNAL_TRANSFER' ||
        bucket === 'INTERNAL TRANSFER' ||
        finzName === 'INTERNAL TRANSFER'
    );
};

const getVisibleNotes = (transaction?: Partial<Transaction> | null, isInternalTransfer?: boolean): string | null => {
    const notes = typeof transaction?.notes === 'string' ? transaction.notes.trim() : '';
    if (!notes) return null;
    if (!isInternalTransfer && /^Linked to\s+\S+$/i.test(notes)) return null;
    return notes;
};

const parseLocalDateString = (value?: string | null): Date | null => {
    if (!value) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, (month || 1) - 1, day || 1);
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatAuditDate = (value: string | { $date: string } | undefined): string => {
    if (!value) return '';
    const raw = typeof value === 'string' ? value : value.$date;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
    });
};

const TransactionDrawer: React.FC<TransactionDrawerProps> = ({
    transactionId,
    open,
    onClose,
    businessId,
    preloadedTransaction,
    normalizationEditorRequest,
    onNormalizationEditorRequestConsumed,
}) => {
    const queryClient = useQueryClient();
    const [formData, setFormData] = useState<Partial<Transaction>>({});
    const [isEditing, setIsEditing] = useState(false);
    const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);
    const [isNormalizationModalOpen, setIsNormalizationModalOpen] = useState(false);
    const [isAccountSelectorOpen, setIsAccountSelectorOpen] = useState(false);
    const [isQBOAccountSelectorOpen, setIsQBOAccountSelectorOpen] = useState(false);
    const [isQboDetailsOpen, setIsQboDetailsOpen] = useState(false);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [vendorModalLookup, setVendorModalLookup] = useState<{ rawNormName: string; direction: string; initialCleanVendorName?: string; autoStartEditing?: boolean } | null>(null);

    const handleAccountSelect = (account: StandardAccount) => {
        const displayName = account.display_name || account.account_name;
        const finzAccountNumber = account.account_number || '';
        const selectedIsInternalTransfer = (displayName || '').trim().toUpperCase() === 'INTERNAL TRANSFER';
        setFormData(prev => ({
            ...prev,
            fiscal_bucket: account.fiscal_bucket,
            finz_account_id: finzAccountNumber,
            finz_account_name: displayName,
            standard_account_id: account._id, // Stable standard_accounts record id
            category: selectedIsInternalTransfer ? 'INTERNAL_TRANSFER' : undefined,
            linked_transfer_id: selectedIsInternalTransfer ? prev.linked_transfer_id : undefined,
            notes: selectedIsInternalTransfer ? prev.notes : (typeof prev.notes === 'string' && /^Linked to\s+\S+$/i.test(prev.notes.trim()) ? '' : prev.notes),
        }));
    };

    const handleQBOAccountSelect = (account: QboAccount) => {
        setFormData(prev => ({
            ...prev,
            qbo_account_id: account.data.account_id,
            qbo_account_name: account.data.fully_qualified_name || account.data.name
        }));
    };

    const { data: fetchedTransaction, isLoading } = useQuery({
        queryKey: ['transaction', transactionId, businessId],
        queryFn: () => transactionApi.getTransaction(transactionId!, businessId),
        enabled: !!transactionId && open && !preloadedTransaction,
    });
    const transaction = preloadedTransaction || fetchedTransaction;

    const { data: linkedAccounts } = useQuery({
        queryKey: ['stripe-linked-accounts', businessId],
        queryFn: () => stripeApi.getLinkedAccounts(businessId),
        enabled: !!open,
        retry: false,
    });

    useEffect(() => {
        if (transaction) {
            setFormData({ ...transaction });
        }
    }, [transaction]);

    useEffect(() => {
        if (!open) {
            setIsEditing(false);
        }
    }, [open]);

    useEffect(() => {
        if (!open || !normalizationEditorRequest?.token) return;
        setIsEditing(true);
        setIsNormalizationModalOpen(true);
        onNormalizationEditorRequestConsumed?.();
    }, [normalizationEditorRequest, onNormalizationEditorRequestConsumed, open]);

    const handleCloseWrapper = () => {
        if (isEditing) {
            setIsEditing(false);
        } else {
            onClose();
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setFormData({ ...transaction });
    };

    const updateMutation = useMutation({
        mutationFn: (data: Partial<Transaction>) => transactionApi.updateTransaction(transactionId!, data, businessId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            queryClient.invalidateQueries({ queryKey: ['transaction', transactionId] });
            if (formData.resolution_status === 'duplicated') {
                setIsEditing(false);
                onClose();
                return;
            }
            setIsEditing(false);
        }
    });

    const handleSave = () => {
        if (preloadedTransaction) {
            setIsEditing(false);
            return;
        }
        if (transactionId) updateMutation.mutate(formData);
    };

    if (!open) return null;

    const matchedFinancialAccount = linkedAccounts?.linked_accounts?.find(
        (acct) => acct.financial_account_id === transaction?.financial_account_id
    );
    const isInternalTransfer = isInternalTransferTransaction(transaction) || isInternalTransferTransaction(formData);
    const visibleNotes = getVisibleNotes(isEditing ? formData : transaction, isInternalTransfer);
    const financialAccountName = matchedFinancialAccount
        ? [matchedFinancialAccount.institution_name, matchedFinancialAccount.display_name].filter(Boolean).join(' - ')
        : (transaction?.financial_account_id || '---');

    return (
        <>
            {/* Backdrop: Fixed opacity issue */}
            <div
                className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 transition-opacity"
                onClick={onClose}
            />

            {/* Drawer Container */}
            <div
                data-tutorial-overlay-blocker="drawer"
                className={`fixed inset-y-0 right-0 w-full mt-12 sm:mt-0 sm:w-[450px] max-w-full bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}
            >

                {/* Header: Consolidated Actions */}
                <div className="p-6 border-b border-gray-100">
                    <div className="flex justify-between items-start mb-4">
                        <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                            <Hash size={20} />
                        </div>
                        <div className="flex gap-1">
                            {!isEditing && (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="p-2 hover:bg-gray-100 rounded-md text-gray-500 transition-colors"
                                >
                                    <Pencil size={18} />
                                </button>
                            )}
                            <button onClick={handleCloseWrapper} className="p-2 hover:bg-gray-100 rounded-md text-gray-500 transition-colors">
                                <X size={18} />
                            </button>
                        </div>
                    </div>

                    <div>
                        {isEditing ? (
                            <input
                                type="text"
                                className="text-xl font-bold text-gray-900 w-full border-b border-gray-300 focus:border-indigo-500 focus:outline-none bg-transparent"
                                value={formData.vendor_name || ''}
                                onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                            />
                        ) : (
                            <h2
                                className="text-xl font-bold text-gray-900 leading-tight hover:text-indigo-600 cursor-pointer transition-colors"
                                onClick={() => {
                                    setVendorModalLookup({
                                        rawNormName: transaction?.normalized_description || '',
                                        direction: transaction?.direction || 'outflow',
                                    });
                                    setIsVendorModalOpen(true);
                                }}
                                title="Click to view Vendor Alias details"
                            >
                                {transaction?.vendor_name || 'Transaction Details'}
                            </h2>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                            <span className="text-3xl font-bold text-gray-900">
                                {transaction?.amount ? formatCurrency(transaction.amount) : '$0.00'}
                            </span>
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-semibold rounded uppercase">
                                {transaction?.currency || 'USD'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Content: Improved Scannability */}
                <div className="flex-1 overflow-y-auto p-6 space-y-8">

                    {/* Flags Section */}
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            disabled={!isEditing}
                            onClick={() => setFormData(prev => ({ ...prev, operating_flag: !prev.operating_flag }))}
                            className={`flex items-center justify-center gap-2 p-3 rounded-lg border text-sm font-medium transition-all ${(isEditing ? formData.operating_flag : transaction?.operating_flag)
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                : 'bg-gray-50 border-gray-200 text-gray-500'
                                }`}
                        >
                            <ShieldCheck size={16} />
                            {formData.operating_flag ? 'Operating' : 'Non-Operating'}
                        </button>
                        <div className="relative">
                            {isEditing ? (
                                <div className="space-y-2">
                                    <select
                                        value={formData.resolution_status || 'needs_review'}
                                        onChange={(e) => setFormData(prev => ({ ...prev, resolution_status: e.target.value }))}
                                        className="w-full appearance-none flex items-center justify-center gap-2 p-3 pl-4 pr-10 rounded-lg border text-sm font-medium transition-all bg-white border-gray-300 text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    >
                                        <option value="needs_review">Needs Review</option>
                                        <option value="verified">Verified</option>
                                        <option value="awaiting_approval">Awaiting Approval</option>
                                        <option value="duplicated">Duplicated</option>
                                    </select>
                                    {formData.resolution_status === 'duplicated' && (
                                        <p className="text-xs text-rose-600">
                                            Saving this will remove the transaction from `bank_normalized` and keep it in raw data with `status=duplicated`.
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div className={`flex items-center justify-center gap-2 p-3 rounded-lg border text-sm font-medium transition-all ${transaction?.resolution_status === 'verified'
                                    ? 'bg-green-50 border-green-200 text-green-700'
                                    : transaction?.resolution_status === 'needs_review'
                                        ? 'bg-red-50 border-red-200 text-red-700'
                                        : 'bg-orange-50 border-orange-200 text-orange-700'
                                    }`}
                                >
                                    <CheckCircle size={16} />
                                    <span className="capitalize">{transaction?.resolution_status?.replace(/_/g, ' ') || 'Needs Review'}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Form Fields */}
                    <div className="space-y-5">
                        <Field label="Finz Category">
                            {isEditing ? (
                                <div
                                    onClick={() => setIsAccountSelectorOpen(true)}
                                    className="w-full p-2 border border-gray-300 rounded-lg shadow-sm text-sm bg-white cursor-pointer hover:border-indigo-500 flex justify-between items-center"
                                >
                                    <span className={formData.fiscal_bucket ? 'text-gray-900' : 'text-gray-400'}>
                                        {formData.fiscal_bucket || 'Select Fiscal Bucket'}
                                    </span>
                                    <Pencil size={14} className="text-gray-400" />
                                </div>
                            ) : (
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryBadgeColor(transaction?.fiscal_bucket)}`}>
                                    {transaction?.fiscal_bucket || 'Uncategorized'}
                                </span>
                            )}
                        </Field>

                        {!isInternalTransfer ? (
                            <>
                                <Field label="Finz Account Name - ID">
                                    <p className="text-sm font-medium text-gray-900">
                                        {isEditing
                                            ? formatFinzAccountDisplay(formData.finz_account_name, formData.finz_account_id)
                                            : formatFinzAccountDisplay(transaction?.finz_account_name, transaction?.finz_account_id)
                                        }
                                    </p>
                                </Field>

                                <Field label="QBO Account Name">
                                    {isEditing ? (
                                        <div className="space-y-2">
                                            {formData.qbo_account_name ? (
                                                <div className="p-2 border border-gray-300 rounded-lg shadow-sm bg-white flex justify-between items-center group">
                                                    <div className="flex flex-col overflow-hidden">
                                                        <span className="text-sm font-medium text-gray-900 truncate" title={formData.qbo_account_name}>
                                                            {formData.qbo_account_name}
                                                        </span>
                                                        {formData.qbo_account_id && (
                                                            <span className="text-[10px] text-gray-400 font-mono">
                                                                {formData.qbo_account_id}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <button
                                                        onClick={() => setIsQBOAccountSelectorOpen(true)}
                                                        className="ml-2 text-xs font-medium text-indigo-600 hover:text-indigo-800 underline flex-shrink-0"
                                                    >
                                                        Change
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => setIsQBOAccountSelectorOpen(true)}
                                                    className="w-full flex items-center justify-center p-2 border border-dashed border-gray-300 rounded-lg text-xs font-medium text-gray-500 hover:border-indigo-500 hover:text-indigo-600 transition-colors bg-gray-50 hover:bg-white"
                                                >
                                                    Select QBO Account
                                                </button>
                                            )}

                                            <QBOAccountSelector
                                                isOpen={isQBOAccountSelectorOpen}
                                                onClose={() => setIsQBOAccountSelectorOpen(false)}
                                                currentAccountId={formData.qbo_account_id}
                                                onSelect={handleQBOAccountSelect}
                                                businessId={businessId}
                                            />
                                        </div>
                                    ) : (
                                        <p className="text-sm font-medium text-gray-900">
                                            {transaction?.qbo_account_name || '---'}
                                        </p>
                                    )}
                                </Field>

                                <Field label="QBO Vendor Name">
                                    <p className="text-sm font-medium text-gray-900">
                                        {transaction?.qbo_vendor_name || '---'}
                                    </p>
                                </Field>
                            </>
                        ) : (
                            <>
                                <Field label="Finz Account Name - ID">
                                    <p className="text-sm font-medium text-gray-900">
                                        {formatFinzAccountDisplay(
                                            (isEditing ? formData.finz_account_name : transaction?.finz_account_name) || 'Internal Transfer',
                                            isEditing ? formData.finz_account_id : transaction?.finz_account_id
                                        )}
                                    </p>
                                </Field>
                                <Field label="Linked Transfer ID">
                                    <p className="text-sm font-medium text-gray-900 font-mono">
                                        {(isEditing ? formData.linked_transfer_id : transaction?.linked_transfer_id) || 'N/A'}
                                    </p>
                                </Field>
                            </>
                        )}



                        <Field label="Notes">
                            {isEditing ? (
                                <textarea
                                    rows={3}
                                    className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                    value={formData.notes || ''}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                />
                            ) : (
                                <p className="text-sm text-gray-600 italic">
                                    {visibleNotes || 'No internal notes added.'}
                                </p>
                            )}
                        </Field>
                    </div>

                    <hr className="border-gray-100" />

                    {/* Metadata Section */}
                    <div className="space-y-4">
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">System Metadata</h3>
                        <div className="grid grid-cols-2 gap-y-4 gap-x-8">
                            <MetaItem label="Transaction ID" value={`#${transactionId?.slice(0, 8)}`} />
                            <MetaItem label="Date" value={parseLocalDateString(transaction?.date)?.toLocaleDateString() || '---'} />
                            <MetaItem
                                label="Direction" value={
                                    <span className="capitalize">
                                        {transaction?.direction || 'Outflow'}
                                    </span>
                                }
                            />
                            <MetaItem
                                label="Status"
                                value={
                                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold uppercase ${(transaction?.status || '').toLowerCase() === 'pending'
                                        ? 'bg-amber-100 text-amber-800'
                                        : (transaction?.status || '').toLowerCase() === 'duplicated'
                                            ? 'bg-rose-100 text-rose-800'
                                            : 'bg-emerald-100 text-emerald-800'
                                        }`}>
                                        {transaction?.status || 'posted'}
                                    </span>
                                }
                            />
                            <MetaItem
                                label="Normalized Desc"
                                value={
                                    isEditing ? (
                                        <div
                                            onClick={() => setIsNormalizationModalOpen(true)}
                                            className="w-full min-w-[180px] cursor-pointer rounded-lg border border-gray-300 bg-white p-2 text-sm shadow-sm transition-colors hover:border-indigo-500"
                                        >
                                            <div className="flex items-center justify-between gap-3">
                                                <span className="truncate text-gray-900">
                                                    {transaction?.normalized_description || 'Set normalized description'}
                                                </span>
                                                <Pencil size={14} className="shrink-0 text-gray-400" />
                                            </div>
                                        </div>
                                    ) : (
                                        <span>{transaction?.normalized_description || '---'}</span>
                                    )
                                }
                            />
                            <MetaItem
                                label="Timestamp"
                                value={
                                    transaction?.timestamp
                                        ? new Date(typeof transaction.timestamp === 'string' ? transaction.timestamp : transaction.timestamp.$date).toLocaleString()
                                        : (parseLocalDateString(transaction?.date)?.toLocaleString() || '---')
                                }
                            />
                            <MetaItem label="Financial Account" value={financialAccountName} />
                            {/* <MetaItem
                                label="QBO Txn List ID"
                                value={
                                    transaction?.qbo_transaction_list_id ? (
                                        <button
                                            type="button"
                                            onClick={() => setIsQboDetailsOpen(true)}
                                            className="font-mono text-xs text-indigo-600 hover:text-indigo-800 underline"
                                        >
                                            {transaction.qbo_transaction_list_id}
                                        </button>
                                    ) : '---'
                                }
                            /> */}
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Original Bank String</label>
                            <p className="text-xs font-mono text-gray-600 break-all">{transaction?.original_description}</p>
                        </div>
                    </div>
                </div>

                {/* Footer: Dynamic Actions */}
                <div className="p-4 border-t border-gray-100 bg-white">
                    {isEditing ? (
                        <div className="flex gap-3">
                            <button
                                onClick={handleCancel}
                                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={updateMutation.isPending}
                                className="flex-1 px-4 py-2.5 bg-indigo-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-indigo-700 shadow-sm transition-colors flex items-center justify-center gap-2"
                            >
                                <Save size={16} />
                                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    ) : (
                        <div className="flex justify-between items-center text-gray-400 italic">
                            <div className="flex flex-col">
                                <p className="text-[11px] whitespace-nowrap">
                                    {transaction?.last_updated_by_display || transaction?.last_updated_by
                                        ? `Last edited by ${transaction?.last_updated_by_display || transaction?.last_updated_by}`
                                        : 'Last edited by System'}
                                    {transaction?.updated_at && ` • ${formatAuditDate(transaction.updated_at)}`}
                                    {(!transaction?.last_updated_by_display && !transaction?.last_updated_by) && !transaction?.updated_at && ' • Jan 2026'}
                                </p>
                                {transaction?.edit_history && transaction.edit_history.length > 0 && (
                                    <button
                                        onClick={() => setIsHistoryModalOpen(true)}
                                        className="text-[10px] text-indigo-500 hover:underline flex items-center gap-1 mt-0.5 not-italic font-medium"
                                    >
                                        <History size={10} />
                                        View Edit History
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Edit History Modal */}
            <EditHistoryModal
                isOpen={isHistoryModalOpen}
                onClose={() => setIsHistoryModalOpen(false)}
                history={transaction?.edit_history}
                vendorName={transaction?.vendor_name}
            />

            {/* Vendor Alias Modal */}
            <VendorAliasModal
                isOpen={isVendorModalOpen}
                onClose={() => {
                    setIsVendorModalOpen(false);
                    setVendorModalLookup(null);
                }}
                rawNormName={vendorModalLookup?.rawNormName || transaction?.normalized_description || ''}
                direction={vendorModalLookup?.direction || transaction?.direction || 'outflow'}
                businessId={businessId}
                transactionId={transactionId}
                initialCleanVendorName={vendorModalLookup?.initialCleanVendorName}
                autoStartEditing={vendorModalLookup?.autoStartEditing}
            />

            <NormalizedDescriptionModal
                isOpen={isNormalizationModalOpen}
                onClose={() => setIsNormalizationModalOpen(false)}
                transaction={transaction}
                businessId={businessId}
                initialSuggestion={normalizationEditorRequest ? {
                    matchType: normalizationEditorRequest.matchType,
                    matchValue: normalizationEditorRequest.matchValue,
                    normalizedDescription: normalizationEditorRequest.normalizedDescription,
                    vendorName: normalizationEditorRequest.vendorName,
                } : null}
                onSaved={({ normalizedDescription, vendorName }: NormalizationSavedPayload) => {
                    setIsNormalizationModalOpen(false);
                    setVendorModalLookup({
                        rawNormName: normalizedDescription,
                        direction: transaction?.direction || 'outflow',
                        initialCleanVendorName: vendorName,
                        autoStartEditing: true,
                    });
                    setIsVendorModalOpen(true);
                }}
            />

            <StandardAccountSelector
                isOpen={isAccountSelectorOpen}
                onClose={() => setIsAccountSelectorOpen(false)}
                onSelect={handleAccountSelect}
                currentAccountId={formData.standard_account_id || null}
                businessId={businessId}
            />

            {isQboDetailsOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30">
                    <div className="w-full max-w-xl rounded-lg bg-white p-5 shadow-xl">
                        <div className="mb-3 flex items-center justify-between">
                            <h3 className="text-base font-semibold text-gray-900">QBO Match Details</h3>
                            <button
                                onClick={() => setIsQboDetailsOpen(false)}
                                className="rounded p-1 text-gray-500 hover:bg-gray-100"
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <MetaItem label="QBO Txn List ID" value={transaction?.qbo_transaction_list_id || '---'} />
                            <MetaItem label="QBO Match Type" value={transaction?.qbo_match_type || '---'} />
                            <MetaItem label="QBO Match Confidence" value={transaction?.qbo_match_confidence ?? '---'} />
                            <MetaItem label="QBO Match Locked" value={String(!!transaction?.qbo_match_locked)} />
                            <MetaItem label="QBO Vendor" value={transaction?.qbo_vendor_name || '---'} />
                            <MetaItem label="QBO Vendor ID" value={transaction?.qbo_vendor_id || '---'} />
                            <MetaItem label="QBO Account" value={transaction?.qbo_account_name || '---'} />
                            <MetaItem label="QBO Account ID" value={transaction?.qbo_account_id || '---'} />
                            <MetaItem label="QBO Split Account" value={transaction?.qbo_split_account || '---'} />
                            <MetaItem label="QBO Txn Type" value={transaction?.qbo_transaction_type || '---'} />
                            <MetaItem label="Link Reason" value={transaction?.qbo_match_link_reason || '---'} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// Helper Components for Cleanliness
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">{label}</label>
        {children}
    </div>
);

const MetaItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div>
        <dt className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">{label}</dt>
        <dd className="text-sm text-gray-700 font-medium mt-0.5">{value}</dd>
    </div>
);

export default TransactionDrawer;
